// Zero-input region resolution (spec 11 §2/§59). The user never picks a region:
//   1. IP-based — GET {base}/v3/geo (restful 00 §4.7c) resolves the caller's IP to
//      a service region. Authoritative when source==="ip".
//   2. OS/locale fallback — when the IP hint is unavailable (source!=="ip" or the
//      call fails), infer from the browser's IANA timezone, then its language region
//      subtag. This covers restful being unreachable or geo detection disabled.
// The resolved region is persisted (config.setRegion) and drives config.apiBase +
// the region field on register/resend/forgot. Best-effort throughout: any failure
// leaves the current/default region in place — resolution never blocks the UI.

import { apiBaseFor, getRegion, regionHosts, setRegion } from '../config';

function pick(region: string | null | undefined): string | null {
  return region && regionHosts.includes(region) ? region : null;
}

/** Map an IANA timezone (e.g. "Asia/Taipei") to a service region. */
export function regionFromTimeZone(tz: string): string | null {
  const t = (tz || '').toLowerCase();
  // Mainland China → isolated cn stack; TW/HK/MO and the rest of Asia → apac.
  if (t === 'asia/shanghai' || t === 'asia/chongqing' || t === 'asia/harbin' || t === 'asia/urumqi' || t === 'asia/kashgar') {
    return pick('cn') ?? pick('apac');
  }
  if (t.startsWith('asia/') || t.startsWith('australia/') || t.startsWith('pacific/') || t.startsWith('indian/')) return pick('apac');
  if (t.startsWith('america/') || t.startsWith('canada/') || t.startsWith('us/') || t.startsWith('mexico/') || t.startsWith('brazil/')) return pick('us');
  if (t.startsWith('europe/') || t.startsWith('africa/') || t.startsWith('atlantic/')) return pick('eu');
  return null;
}

/** OS/browser fallback: timezone first, then the language region subtag. */
export function regionFromBrowser(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const byTz = regionFromTimeZone(tz);
    if (byTz) return byTz;
  } catch {
    // Intl unavailable — fall through to language.
  }
  const cc = (navigator.language || '').toUpperCase().match(/[-_]([A-Z]{2})$/)?.[1];
  if (cc === 'CN') return pick('cn') ?? pick('apac');
  if (cc === 'US') return pick('us');
  if (cc === 'TW' || cc === 'HK' || cc === 'MO' || cc === 'JP' || cc === 'KR' || cc === 'AU' || cc === 'SG') return pick('apac');
  return null;
}

/** Resolve and persist the region: IP hint first, OS/locale fallback second. */
export async function detectRegion(signal?: AbortSignal): Promise<string> {
  try {
    const res = await fetch(apiBaseFor(getRegion()) + '/v3/geo', { method: 'GET', signal });
    if (res.ok) {
      const body = (await res.json()) as { data?: { region?: string; source?: string } };
      const region = body.data?.region;
      // Re-check the signal before persisting: if the user authenticated while the
      // request was in flight, the account's region is now authoritative and
      // switching the host here would break the session (multi-region deployments).
      if (body.data?.source === 'ip' && pick(region) && !signal?.aborted) {
        setRegion(region!);
        return region!;
      }
    }
  } catch {
    // network/abort — fall through to OS/locale detection.
  }
  const fallback = regionFromBrowser();
  if (fallback && !signal?.aborted) setRegion(fallback);
  return getRegion();
}
